import { useEffect, useState } from "react";
import { Card, Table, Tag, Input, Select, Row, Col, Typography, Avatar, Empty, Tabs } from "antd";
import { SearchOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { useGetMyTripList } from "../services/traveller.service";
import { storage } from "../utils/storage";

const { Text } = Typography;
const { Option } = Select;

const statusColors: Record<string, string> = {
    Pending: "orange",
    Upcoming: "blue",
    Running: "green",
    Completed: "cyan",
    Cancelled: "red",
};

const filterTabs = [
    { key: "all", label: "All Trips" },
    { key: "upcoming", label: "Upcoming" },
    { key: "running", label: "Running" },
    { key: "completed", label: "Completed" },
    { key: "cancelled", label: "Cancelled" },
];

const MyTrips = () => {
    const userId = storage.getUserId() ?? "";
    const [activeFilter, setActiveFilter] = useState("all");
    const [searchFilter, setSearchFilter] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [trips, setTrips] = useState<any[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const pageSize = 10;

    const fetchTrips = useGetMyTripList({
        onSuccess: (res: any) => {
            if (res?.statusCode === 200) {
                const data: any[] = res.data ?? [];
                setTrips(data);
                setTotalRecords(data[0]?.recordCount ?? data.length);
            } else {
                setTrips([]);
                setTotalRecords(0);
            }
        },
    });

    useEffect(() => {
        if (!userId) return;
        fetchTrips.mutate({ pageNumber, pageSize, filterType: activeFilter, searchFilter });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilter, pageNumber, searchFilter, userId]);

    const handleSearch = (value: string) => {
        setSearchFilter(value);
        setPageNumber(1);
    };

    const handleTabChange = (key: string) => {
        setActiveFilter(key);
        setPageNumber(1);
        setSearchFilter("");
    };

    const columns = [
        {
            title: "Attendant",
            dataIndex: "agentName",
            render: (name: string, record: any) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar size={36} src={record.agentImageUrl} style={{ background: "#6366f1" }}>
                        {(name ?? "A").charAt(0)}
                    </Avatar>
                    <div>
                        <Text style={{ fontWeight: 500, fontSize: 13 }}>{name ?? "Not assigned"}</Text>
                        {record.noOfPax && (
                            <Text style={{ color: "#94a3b8", fontSize: 12, display: "block" }}>
                                {record.noOfPax} pax
                            </Text>
                        )}
                    </div>
                </div>
            ),
        },
        {
            title: "Destination",
            dataIndex: "cities",
            render: (city: string) => (
                <span>
                    <EnvironmentOutlined style={{ color: "#6366f1", marginRight: 4 }} />
                    <Text style={{ fontSize: 13 }}>{city ?? "—"}</Text>
                </span>
            ),
        },
        {
            title: "Trip Date",
            dataIndex: "tripStartDate",
            render: (d: string) =>
                d
                    ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                    : "—",
        },
        {
            title: "Pickup",
            dataIndex: "pickupAddress",
            render: (addr: string) => (
                <Text style={{ fontSize: 12, color: "#64748b", maxWidth: 180, display: "block" }} ellipsis={{ tooltip: addr }}>
                    {addr ?? "—"}
                </Text>
            ),
        },
        {
            title: "Status",
            dataIndex: "tripStatus",
            render: (status: string) => <Tag color={statusColors[status] ?? "default"}>{status ?? "—"}</Tag>,
        },
        {
            title: "Agent Status",
            dataIndex: "agentStatus",
            render: (status: string) =>
                status ? <Tag color="geekblue">{status}</Tag> : <Text style={{ color: "#cbd5e1" }}>—</Text>,
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <Text style={{ fontSize: 22, fontWeight: 700, color: "#1e293b", display: "block" }}>My Trips</Text>
                <Text style={{ color: "#64748b", fontSize: 14 }}>View and manage all your trips in one place.</Text>
            </div>

            <Card
                style={{ borderRadius: 14, border: "none", boxShadow: "0 1px 6px rgba(0,0,0,0.07)" }}
                bodyStyle={{ padding: 0 }}
            >
                {/* Tabs */}
                <Tabs
                    activeKey={activeFilter}
                    onChange={handleTabChange}
                    style={{ padding: "0 20px" }}
                    items={filterTabs.map((tab) => ({ key: tab.key, label: tab.label }))}
                />

                {/* Search */}
                <div style={{ padding: "0 20px 16px" }}>
                    <Row gutter={12}>
                        <Col xs={24} md={10}>
                            <Input
                                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                                placeholder="Search by city, attendant…"
                                value={searchFilter}
                                onChange={(e) => handleSearch(e.target.value)}
                                style={{ borderRadius: 8 }}
                                allowClear
                            />
                        </Col>
                    </Row>
                </div>

                <Table
                    dataSource={trips}
                    columns={columns}
                    rowKey={(r) => r.tripId ?? Math.random()}
                    loading={fetchTrips.isLoading}
                    pagination={{
                        current: pageNumber,
                        pageSize,
                        total: totalRecords,
                        onChange: (page) => setPageNumber(page),
                        showSizeChanger: false,
                        showTotal: (total) => `${total} trips`,
                        style: { padding: "12px 20px" },
                    }}
                    locale={{
                        emptyText: (
                            <Empty
                                style={{ padding: "32px 0" }}
                                description={<Text style={{ color: "#94a3b8" }}>No trips found</Text>}
                                imageStyle={{ height: 60 }}
                            />
                        ),
                    }}
                    scroll={{ x: 800 }}
                    style={{ borderRadius: "0 0 14px 14px" }}
                />
            </Card>
        </div>
    );
};

export default MyTrips;
